var _a;
import { VOCABULARY_TYPES } from './inputTypeOptions';
import { isMultiValueSubfield } from '../../../components/ProposalForm/utils/multiValueSubfield';
/** A type ref (recordType/vocabularyType value) is a vocabulary when it carries
 *  the VOCABULARY_ prefix; anything else is an authority record type. */
var isVocabRef = function (v) {
    return typeof v === 'string' && v.startsWith('VOCABULARY_');
};
/** Vocabulary type seeded when an admin switches a subfield to a vocabulary kind
 *  that doesn't yet carry a concrete type. A vocabulary picker is meaningless
 *  without a type, and — critically — leaving it blank makes {@link getInputKind}
 *  read the subfield back as `authority` (the typeless-picker fallback). That was
 *  the reported bug: selecting "Slovníková položka" on a plain-text subfield
 *  (e.g. 400$i) snapped straight back to "Autorita" and hid the "Typ slovníku"
 *  select, so the vocabulary could never be chosen. Seeding a concrete default
 *  keeps the kind stable and surfaces the type select for the admin to adjust. */
var DEFAULT_VOCABULARY_TYPE = (_a = VOCABULARY_TYPES[0]) !== null && _a !== void 0 ? _a : 'VOCABULARY_LANGUAGE';
export var INPUT_KINDS = [
    'text',
    'text-multi',
    'textarea',
    'textarea-multi',
    'authority',
    'authorities',
    'vocab-item',
    'vocab-items',
];
/**
 * MARC authority record types (per project business rules). Used as
 * the `recordType` value for `authority` / `authorities` kinds — the
 * picker filters its result set to one of these.
 *
 * This list is also the option set of SubfieldEditor's "record type" `<Select>`,
 * so a target that a shipped form-config uses but this array omits renders as a
 * `<Select>` whose value matches no option: the admin sees it blank and the next
 * save writes the blank back, silently dropping the picker target. CZNA-458 added
 * GENRE_FORM for exactly that reason — genre-form.json points its three 555
 * hierarchy pickers at GENRE_FORM. Every entry needs a matching
 * `settings.forms.visual.property.recordType.{TYPE}` translation.
 *
 * CZNA-181: OLD_PRINT_CITY (prefix pv, MARC 195) is deliberately NOT listed —
 * old-print imprint cities have no inter-record relations and are not a
 * documented record-picker target (they are not a controlled place vocabulary;
 * cf. QualifierParser non-participation). Revisit only if NK asks for it.
 *
 * CHRONOLOGICAL is likewise absent: chronological.json declares no picker, and
 * the corpus has no 5XX tracing on any of its 631 records.
 */
export var AUTHORITY_RECORD_TYPES = [
    'PERSONAL',
    'CORPORATE',
    'MEETING',
    'UNIFORM_TITLE',
    'SUBJECT',
    'GEOGRAPHIC',
    'GENRE_FORM',
];
/**
 * Reverse-map a SubfieldDef to its kind. Multi-modality is signalled by
 * `inputType: 'multi-record-picker'` (multi authority — CZNA-214),
 * `inputType: 'multi-vocabulary'` (multi vocabulary), or the legacy
 * `multiValue` boolean flag — all trigger the multi component in the renderer.
 *
 * Authority-vs-vocabulary discrimination (CZNA-214):
 *   - `multi-record-picker` is, by itself, the canonical multi-AUTHORITY kind.
 *   - `multi-vocabulary` is the canonical multi-VOCABULARY kind — UNLESS it
 *     carries a non-VOCABULARY_ `recordType`, which is the legacy hand-authored
 *     shape for multi authorities (kept working for backward compatibility).
 * So `inputType` alone now reliably discriminates for newly-written configs
 * (`multi-record-picker` ⇒ authority, `multi-vocabulary` ⇒ vocabulary), while
 * the `recordType`/`vocabularyType` value remains the tiebreaker for legacy
 * `multi-vocabulary` rows.
 *
 * The vocabulary-vs-authority axis is otherwise decided by the `recordType` /
 * `vocabularyType` value: VOCABULARY_* prefixed strings are vocab entries,
 * anything else is an authority record. If neither is set (broken or fresh
 * config) we default to `authority` so the admin has a starting point.
 *
 * `inputType: 'vocabulary'` is a legacy alias for `'record-picker'` —
 * treated identically here so the dropdown reads consistently.
 */
export function getInputKind(sf) {
    if (sf.inputType === 'textarea')
        return 'textarea';
    if (sf.inputType === 'multi-textarea')
        return 'textarea-multi';
    // Free-text multi-value (TASK-110): `multi-text` inputType, or a multiValue
    // subfield with no picker backing (no record/vocabulary type). Renders the
    // chip-input free-text component, NOT a picker.
    if (sf.inputType === 'multi-text'
        || (sf.multiValue === true
            && sf.inputType !== 'multi-vocabulary'
            && sf.inputType !== 'multi-record-picker'
            && !sf.recordType
            && !sf.vocabularyType)) {
        return 'text-multi';
    }
    // CZNA-214: the dedicated multi-AUTHORITY value short-circuits — it is the
    // authorities kind regardless of which type field carries the recordType.
    // CZNA-1646: unless what it picks from is a vocabulary. The packaged templates
    // make a register picker repeatable by keeping its picker (`375 $a` gender,
    // `377 $a` language, `368 $d` title, `680 $i`), so `multi-record-picker` there
    // carries a VOCABULARY_* type: read as `authorities`, the editor would offer the
    // authority types and drop the vocabulary on the next kind change.
    if (sf.inputType === 'multi-record-picker') {
        return isVocabRef(sf.vocabularyType) || isVocabRef(sf.recordType) ? 'vocab-items' : 'authorities';
    }
    var isPicker = sf.inputType === 'record-picker'
        || sf.inputType === 'vocabulary'
        || sf.inputType === 'multi-vocabulary';
    if (!isPicker) {
        // text, 'select', 'combobox', undefined → text. select/combobox
        // are deprecated for new configs (see inputTypeOptions.ts).
        return 'text';
    }
    // Behaviour-identical to the old `inputType === 'multi-vocabulary' || multiValue`
    // reading — `multi-record-picker` and `multi-text` both short-circuit above, so
    // `multi-vocabulary` is the only multi inputType that reaches here. Routed
    // anyway (CZNA-567): this file's own comment forbids a second hand-written
    // reading of either key, and `getInputKind` drives the editor dropdown and
    // `defaultValueSource.authorityPickerTarget`, so it is load-bearing drift
    // surface rather than a local detail.
    var isMulti = isMultiValueSubfield(sf);
    var ref = sf.recordType || sf.vocabularyType;
    var isVocab = typeof ref === 'string' && ref.startsWith('VOCABULARY_');
    if (isVocab)
        return isMulti ? 'vocab-items' : 'vocab-item';
    return isMulti ? 'authorities' : 'authority';
}
/**
 * Forward-map a kind into a SubfieldDef patch. The reducer treats
 * `undefined` values as key deletions so the resulting JSON stays
 * clean (no `"vocabularyType": null` leftovers).
 *
 * For authority kinds we standardise on `recordType` and clear
 * `vocabularyType`; for vocab kinds we standardise on `vocabularyType`
 * and clear `recordType`. This nudges the schema toward a single
 * canonical convention even though the renderer accepts either.
 *
 * `multiValue` is cleared on the SINGLE-value kinds, where it would
 * contradict the `inputType`, and SET on the multi kinds.
 *
 * Setting it is no longer load-bearing — CZNA-567 moved every codec branch
 * onto `isMultiValueSubfield` (`ProposalForm/utils/multiValueSubfield.ts`),
 * which reads the `multi-*` `inputType` this function writes. It is kept
 * because it costs nothing and keeps the emitted JSON self-describing beside
 * the hand-authored configs that still carry the flag alone.
 *
 * The history is worth keeping, because the trap was invisible from here: this
 * function used to clear `multiValue` on EVERY kind, on the stated theory that
 * "the picker direction is driven by `inputType` alone". That was true of the
 * renderer and false of the codec, which keyed on `multiValue` and read no
 * `inputType` at all — so every multi-value subfield an admin created here was
 * born unsaveable, and merely opening a working one and saving it stripped the
 * flag off (CZNA-378). CZNA-567 removed the disagreement rather than the
 * symptom; do not reintroduce a second reading of either key.
 *
 * The admin still refines the concrete type via the "Typ záznamu" /
 * "Typ slovníku" select, but the patch always leaves the subfield in a
 * state that {@link getInputKind} maps back to the SAME kind — so the
 * dropdown selection is stable. To guarantee that, pass the current
 * subfield: an existing concrete type is preserved (and a vocab type that
 * was stored in `recordType` is migrated to `vocabularyType`), and a fresh
 * vocab kind is seeded with {@link DEFAULT_VOCABULARY_TYPE}. Omitting
 * `current` is supported (seeds the vocab default, leaves authority open).
 */
export function applyInputKind(kind, current) {
    // Split the current concrete type by axis. A VOCABULARY_* value counts as the
    // vocab type wherever it sits (recordType or vocabularyType — both conventions
    // exist in real configs); a non-vocab recordType is the authority type.
    var recordTypeIsVocab = isVocabRef(current === null || current === void 0 ? void 0 : current.recordType);
    var existingVocab = isVocabRef(current === null || current === void 0 ? void 0 : current.vocabularyType)
        ? current === null || current === void 0 ? void 0 : current.vocabularyType
        : recordTypeIsVocab
            ? current === null || current === void 0 ? void 0 : current.recordType
            : undefined;
    var existingAuthority = recordTypeIsVocab ? undefined : current === null || current === void 0 ? void 0 : current.recordType;
    switch (kind) {
        case 'text':
            return {
                inputType: undefined,
                recordType: undefined,
                vocabularyType: undefined,
                multiValue: undefined,
            };
        case 'text-multi':
            // Free-text chip list (TASK-110). `multi-text` inputType drives the
            // free-text renderer branch; `multiValue` drives the codec's values[]
            // serialization. Both are required; clear any picker backing.
            return {
                inputType: 'multi-text',
                multiValue: true,
                recordType: undefined,
                vocabularyType: undefined,
                suggestVocabularyType: undefined,
            };
        case 'textarea':
            return {
                inputType: 'textarea',
                recordType: undefined,
                vocabularyType: undefined,
                multiValue: undefined,
                suggestVocabularyType: undefined,
            };
        case 'textarea-multi':
            return {
                inputType: 'multi-textarea',
                multiValue: true,
                recordType: undefined,
                vocabularyType: undefined,
                suggestVocabularyType: undefined,
            };
        case 'authority':
            return {
                inputType: 'record-picker',
                recordType: existingAuthority,
                vocabularyType: undefined,
                multiValue: undefined,
                suggestVocabularyType: undefined,
            };
        case 'authorities':
            // CZNA-214: write the dedicated multi-AUTHORITY inputType so that
            // `inputType` alone discriminates authority vs vocabulary. Previously
            // this emitted `'multi-vocabulary'` (shared with multi VOCABULARY),
            // which made `inputType` an unreliable discriminator. Legacy configs
            // still carrying `'multi-vocabulary'` + a non-VOCABULARY_ recordType
            // keep working via getInputKind's tiebreaker.
            return {
                inputType: 'multi-record-picker',
                recordType: existingAuthority,
                vocabularyType: undefined,
                multiValue: true,
                suggestVocabularyType: undefined,
            };
        case 'vocab-item':
            return {
                inputType: 'record-picker',
                vocabularyType: existingVocab !== null && existingVocab !== void 0 ? existingVocab : DEFAULT_VOCABULARY_TYPE,
                recordType: undefined,
                multiValue: undefined,
                suggestVocabularyType: undefined,
            };
        // `vocab-items` is the last InputKind; `default` keeps the switch total
        // (consistent-return, default-case) while TypeScript still checks it exhaustively.
        default:
            return {
                inputType: 'multi-vocabulary',
                vocabularyType: existingVocab !== null && existingVocab !== void 0 ? existingVocab : DEFAULT_VOCABULARY_TYPE,
                recordType: undefined,
                multiValue: true,
                suggestVocabularyType: undefined,
            };
    }
}
/** True if this kind needs the admin to pick a `recordType` value. */
export var kindUsesRecordType = function (kind) {
    return kind === 'authority' || kind === 'authorities';
};
/**
 * CZNA-1471 — true if this kind may carry a quick-pick vocabulary
 * (`suggestVocabularyType`). Only plain text: the quick pick inserts text into an
 * input the cataloguer keeps typing in, which no other kind is. `applyInputKind`
 * clears the key on every other kind, and keeps it on `text`.
 */
export var kindUsesSuggestVocabulary = function (kind) { return kind === 'text'; };
/** True if this kind needs the admin to pick a `vocabularyType` value. */
export var kindUsesVocabularyType = function (kind) {
    return kind === 'vocab-item' || kind === 'vocab-items';
};
